import {layoutActions} from 'features/layout/redux';
import {call, put, select} from 'redux-saga/effects';
import i18n from 'i18n';
import {asyncSagaConstructor, getErrors} from 'redux/common';
import api from 'api';
import ApiService from 'api/ApiService';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {licenseSelectors} from 'redux/license';
import {backupTypes} from 'redux/backuprestore';
import {waitUntilAllJobsStopped} from '../waitUntilAllJobsStopped';
import {IRestoreResponse} from 'types/diplomat/backup/RestoreResponse';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {DialogTypes} from 'common/names';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';

let i = 0;
const lockMsg = i++,
   suspendMsg = i++,
   stoppedMsg = i++,
   backupMsg = i++,
   restoreMsg = i++,
   doneMsg = i++,
   userValidationMsg = i++,
   unsuspendMsg = i++,
   unlockMsg = i++;
let lines: string[];

export function* initRestoreDialogProcess(
   filePath: string,
   config: boolean,
   password: string,
   disconnect = false
): Generator<any> {
   let buttonLabel = i18n.t('Cancel');
   const isFeatureSupported = (yield select(licenseSelectors.isFeatureSupported)) as ReturnType<
      typeof licenseSelectors.isFeatureSupported
   >;
   let retryRequired = false;

   try {
      const resp = (yield call(
         startRestoreProcess,
         filePath,
         config,
         password,
         disconnect
      )) as IRestoreResponse;
      yield call(parseResponse, resp);

      yield call(cleanup);

      yield put(layoutActions.loadTreeNav());
   } catch (e) {
      if ((e as Response).status === 409) {
         const dialogResult = (yield call(openDialogWithResult, {
            id: DialogTypes.ConfirmDialog.id,
            data: {
               confirmLabel: i18n.t('Disconnect'),
               title: i18n.t('Current Connections'),
               content: i18n.t(
                  'You cannot complete a restore when other users are connected.  Select "Disconnect" to\n' +
                     'disconnect all users shown below and continue. Select "Cancel" to cancel the restore.'
               ),
            },
         })) as ICloseSubDialogAction<boolean>;

         if (dialogResult.payload?.data) {
            retryRequired = true;
         }
      } else {
         yield call(handleError, e);
      }
   } finally {
      try {
         //if we are in a mode that does not allow for unsuspending jobs
         //from the menu make sure that jobs are unsuspended when we leave
         if (!isFeatureSupported(DiplomatFeature.JOB_SUSPENSION)) {
            //unsuspend all jobs
            yield call(
               asyncSagaConstructor(api.releaseRestore, backupTypes.RELEASE_RESTORE, {
                  getMethod: ApiService.postSecureJson,
               })
            );
            lines[unsuspendMsg] = i18n.t('Job scheduling has been unsuspended');
            yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
         }

         yield call(
            asyncSagaConstructor(api.unlock, backupTypes.UNLOCK, {
               getMethod: ApiService.postSecureJson,
            })
         );
         lines[unlockMsg] = i18n.t('Server has been unlocked');
         buttonLabel = i18n.t('Close');
         yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

         if (retryRequired) {
            yield call(initRestoreDialogProcess, filePath, config, password, true);
            retryRequired = false;
         }
      } catch (e) {
         yield call(handleError, e);
      }
   }
}

function* handleError(e: any) {
   const {message} = yield call(getErrors, e, '');
   // console.debug(e, message);
   lines[doneMsg] = `${message}\n ${i18n.t('Restore process has been cancelled.')}`;
   const buttonLabel = i18n.t('Close');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
}

function* parseResponse(resp: IRestoreResponse) {
   // console.debug(resp);
   const buttonLabel = i18n.t('Close');
   if (resp.status === null || resp.status === true) {
      lines[restoreMsg] = i18n.t('Transaction datastore restored');
      lines[doneMsg] = resp.statusText;
      if (resp.usersAdded) {
         lines[userValidationMsg] = i18n.t(
            'New user accounts have been restored to your active ' +
               'user account list.  Please review user accounts under ' +
               'Settings > User Accounts to review and update.'
         );
      }
   } else {
      lines[restoreMsg] = resp.errors;
      lines[doneMsg] = i18n.t('Restore process has been cancelled.');
   }
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
}

function* startRestoreProcess(
   filePath: string,
   config: boolean,
   password: string,
   disconnect: boolean
) {
   let buttonLabel = i18n.t('Cancel');
   lines = [];

   lines[lockMsg] = i18n.t('Locking server...');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

   //lock the server to new logins
   yield call(
      asyncSagaConstructor(api.lockforrestore, backupTypes.LOCK_FOR_RESTORE, {
         getMethod: ApiService.postSecureJson,
      })
   );
   lines[lockMsg] = i18n.t('Server has been locked');
   lines[suspendMsg] = i18n.t('Suspending job scheduling...');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

   //stop transaction startup and suspend jobs
   yield call(
      asyncSagaConstructor(api.prepareforrestore, backupTypes.PREPARE_FOR_RESTORE, {
         getMethod: ApiService.postSecureJson,
      })
   );
   lines[suspendMsg] = i18n.t('Job scheduling has been suspended');
   lines[stoppedMsg] = i18n.t('Waiting for current jobs to stop...');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

   //wait for running jobs to finish
   yield call(waitUntilAllJobsStopped);
   lines[stoppedMsg] = i18n.t('All jobs have stopped');
   lines[backupMsg] = i18n.t('Backing up current transaction datastore...');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

   // backup
   const {result: path} = yield call(
      asyncSagaConstructor(api.backup, backupTypes.BACKUP, {
         getMethod: ApiService.postSecureJson,
         data: {},
      })
   );
   lines[backupMsg] = i18n.t('Transaction datastore backed up to \n {{path}}', {path});
   lines[restoreMsg] = i18n.t('Restoring transaction datastore...');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

   //restore
   const resp = (yield call(
      asyncSagaConstructor(api.restore, backupTypes.RESTORE, {
         getMethod: ApiService.postSecureJson,
         data: {
            disconnect,
            filePath,
            password,
            restoreConfigSettings: config,
         },
         hideErrorToast: true,
      })
   )) as IRestoreResponse;

   return resp;
}

function* cleanup() {
   const buttonLabel = i18n.t('Close');

   const isFeatureSupported = (yield select(licenseSelectors.isFeatureSupported)) as ReturnType<
      typeof licenseSelectors.isFeatureSupported
   >;

   //if we are in a mode that allows for unsuspending jobs from the
   //menu see if user wants to unsuspend jobs now
   if (isFeatureSupported(DiplomatFeature.JOB_SUSPENSION)) {
      const dialogResult = (yield call(openDialogWithResult, {
         id: DialogTypes.ConfirmDialog.id,
         data: {
            confirmLabel: i18n.t('Yes'),
            cancelLabel: i18n.t('No'),
            title: i18n.t('Release Job Suspension'),
            content: i18n.t(
               'Do you want to release the DB restore job suspension?\n' +
                  "NOTE:  Selecting 'Yes' will not release jobs that were already suspended at the time the backup was created."
            ),
         },
      })) as ICloseSubDialogAction<boolean>;

      if (dialogResult.payload?.data) {
         yield call(
            asyncSagaConstructor(api.releaseRestore, backupTypes.RELEASE_RESTORE, {
               getMethod: ApiService.postSecureJson,
            })
         );
         lines[unsuspendMsg] = i18n.t('Job scheduling has been unsuspended');
         yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
      }
   }
}
